// src/pages/MyProductsPage.tsx - VERSION CORRIGÉE avec vraies statistiques
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import productService, { Product } from '../services/productService';
import ProductManagementTable from '../components/ProductManagementTable';
import { RefreshCw, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';

const MyProductsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'advanced'>('table');
  const [loadingRealStats, setLoadingRealStats] = useState(false);
  const [useRealStats, setUseRealStats] = useState(true); // ✅ NOUVEAU - Option pour vraies stats

  // Vérifier que l'utilisateur est un farmer
  useEffect(() => {
    if (user && !user.is_farmer) {
      navigate('/');
    }
  }, [user, navigate]);

  // Charger les produits du farmer
  useEffect(() => {
    if (user?.is_farmer) {
      loadProducts();
    }
  }, [user, useRealStats]);

  // ✅ NOUVELLE MÉTHODE - Charger les produits avec vraies statistiques
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (useRealStats) {
        console.log('📊 Chargement avec vraies statistiques...');
        const farmerProducts = await productService.getFarmerProductsWithRealStats();
        setProducts(farmerProducts);
        console.log('✅ Produits chargés avec vraies stats');
      } else {
        console.log('📦 Chargement standard...');
        const farmerProducts = await productService.getFarmerProducts();
        setProducts(farmerProducts);
        console.log('✅ Produits chargés (stats statiques)');
      }
      
    } catch (err: any) {
      console.error('Erreur lors du chargement des produits:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        // ✅ Fallback: Essayer le chargement standard si les vraies stats échouent
        if (useRealStats) {
          console.warn('⚠️ Fallback vers stats statiques...');
          try {
            const farmerProducts = await productService.getFarmerProducts();
            setProducts(farmerProducts);
            setError('Statistiques en temps réel non disponibles. Affichage des données en cache.');
          } catch (fallbackErr) {
            setError('Erreur lors du chargement des produits. Veuillez réessayer.');
          }
        } else {
          setError('Erreur lors du chargement des produits. Veuillez réessayer.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOUVELLE MÉTHODE - Rafraîchir les vraies statistiques
  const refreshRealStats = async () => {
    try {
      setLoadingRealStats(true);
      console.log('🔄 Rafraîchissement des vraies statistiques...');
      
      const farmerProducts = await productService.getFarmerProductsWithRealStats();
      setProducts(farmerProducts);
      setError(''); // Nettoyer les erreurs précédentes
      
      console.log('✅ Statistiques rafraîchies avec succès');
      
    } catch (err: any) {
      console.error('Erreur lors du rafraîchissement:', err);
      setError('Erreur lors du rafraîchissement des statistiques.');
    } finally {
      setLoadingRealStats(false);
    }
  };

  const handleProductDeleted = async (productId: number) => {
    try {
      await productService.deleteProduct(productId);
      setProducts(products.filter(product => product.id !== productId));
      alert('Produit supprimé avec succès');
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      if (err.response?.data?.detail) {
        alert(`Erreur: ${err.response.data.detail}`);
      } else {
        alert('Erreur lors de la suppression du produit');
      }
    }
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) {
      return { text: 'Épuisé', color: 'text-red-600 bg-red-100' };
    } else if (stock <= 5) {
      return { text: 'Stock faible', color: 'text-yellow-600 bg-yellow-100' };
    } else {
      return { text: 'En stock', color: 'text-green-600 bg-green-100' };
    }
  };

  // ✅ NOUVELLE MÉTHODE - Obtenir les vraies quantités vendues
  const getRealSoldQuantity = (product: Product): number => {
    return productService.getRealSoldQuantity(product);
  };

  // ✅ NOUVELLE MÉTHODE - Vérifier si les vraies stats sont disponibles
  const hasRealStats = (product: Product): boolean => {
    return product.real_sold !== undefined;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement de vos produits...</p>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button 
          onClick={loadProducts}
          className="bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate mb-2">Mes Produits</h1>
          <p className="text-gray-600">Gérez votre catalogue de produits</p>
          {/* ✅ NOUVEAU - Indicateur de type de statistiques */}
          {products.length > 0 && (
            <div className="flex items-center mt-2 text-sm">
              {useRealStats && products.some(hasRealStats) ? (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Statistiques en temps réel
                </div>
              ) : (
                <div className="flex items-center text-yellow-600">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Statistiques en cache
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          {/* ✅ NOUVEAU - Bouton rafraîchir stats */}
          {useRealStats && (
            <button
              onClick={refreshRealStats}
              disabled={loadingRealStats}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center disabled:opacity-50"
              title="Rafraîchir les statistiques"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingRealStats ? 'animate-spin' : ''}`} />
              {loadingRealStats ? 'Actualisation...' : 'Actualiser'}
            </button>
          )}
          
          <Link 
            to="/dashboard"
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Tableau de bord
          </Link>
          <Link 
            to="/add-product"
            className="bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter un produit
          </Link>
        </div>
      </div>

      {/* Message d'avertissement si statistiques en cache */}
      {error && products.length > 0 && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={refreshRealStats}
            className="ml-auto text-yellow-800 hover:text-yellow-900 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Sélecteur de mode d'affichage */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Mode d'affichage:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                    viewMode === 'table' 
                      ? 'bg-white text-olive shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  📋 Tableau simple
                </button>
                <button
                  onClick={() => setViewMode('advanced')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                    viewMode === 'advanced' 
                      ? 'bg-white text-olive shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  ⚙️ Gestion avancée
                </button>
              </div>
              
              {/* ✅ NOUVEAU - Toggle pour vraies statistiques */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Stats temps réel:</span>
                <button
                  onClick={() => setUseRealStats(!useRealStats)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    useRealStats ? 'bg-olive' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    useRealStats ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* Alertes de stock */}
            {products.length > 0 && (
              <div className="flex items-center space-x-4 text-sm">
                {products.filter(p => p.stock === 0).length > 0 && (
                  <div className="flex items-center text-red-600">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {products.filter(p => p.stock === 0).length} produit(s) épuisé(s)
                  </div>
                )}
                {products.filter(p => p.stock > 0 && p.stock <= 5).length > 0 && (
                  <div className="flex items-center text-yellow-600">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {products.filter(p => p.stock > 0 && p.stock <= 5).length} stock(s) faible(s)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contenu selon le mode d'affichage */}
      {products.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun produit pour le moment</h3>
          <p className="text-gray-600 mb-6">
            Commencez à vendre en ajoutant votre premier produit à votre catalogue.
          </p>
          <Link 
            to="/add-product"
            className="bg-olive text-white px-6 py-3 rounded-md hover:bg-terracotta transition inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter mon premier produit
          </Link>
        </div>
      ) : viewMode === 'advanced' ? (
        <ProductManagementTable 
          products={products}
          onProductUpdated={loadProducts}
          onProductDeleted={handleProductDeleted}
        />
      ) : (
        <>
          {/* ✅ Statistiques rapides avec vraies données */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Total produits</h3>
              <p className="text-2xl font-bold text-slate">{products.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-1">En stock</h3>
              <p className="text-2xl font-bold text-green-600">
                {products.filter(p => p.stock > 0).length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Épuisés</h3>
              <p className="text-2xl font-bold text-red-600">
                {products.filter(p => p.stock === 0).length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Total vendus</h3>
                  <p className="text-2xl font-bold text-terracotta">
                    {products.reduce((sum, p) => sum + getRealSoldQuantity(p), 0)}
                  </p>
                </div>
                {/* ✅ NOUVEAU - Indicateur de type de stats */}
                {useRealStats && products.some(hasRealStats) && (
                  <TrendingUp className="h-6 w-6 text-green-500" title="Statistiques en temps réel" />
                )}
              </div>
            </div>
          </div>

          {/* Liste des produits - Vue simple avec vraies stats */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Vue desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {/* ✅ NOUVEAU - En-tête avec indicateur */}
                      <div className="flex items-center space-x-1">
                        <span>Vendus</span>
                        {useRealStats && products.some(hasRealStats) && (
                          <TrendingUp className="h-3 w-3 text-green-500" title="Temps réel" />
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => {
                    const stockStatus = getStockStatus(product.stock);
                    const realSold = getRealSoldQuantity(product);
                    const isRealTime = hasRealStats(product);
                    
                    return (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              <img 
                                className="h-12 w-12 rounded-lg object-cover border"
                                src={productService.getImageUrl(product.image_url)} 
                                alt={product.name}
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-image.jpg';
                                }}
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500 max-w-xs truncate">
                                {product.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{product.price.toLocaleString()} FCFA</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{product.stock} unités</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* ✅ CORRECTION PRINCIPALE - Utiliser les vraies stats */}
                          <div className="flex items-center space-x-1">
                            <span className="text-sm text-gray-900">{realSold} unités</span>
                            {isRealTime && useRealStats && (
                              <CheckCircle className="h-3 w-3 text-green-500" title="Données en temps réel" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                            {stockStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Link 
                              to={`/edit-product/${product.id}`}
                              className="text-olive hover:text-terracotta transition"
                            >
                              Modifier
                            </Link>
                            <button 
                              onClick={() => handleProductDeleted(product.id)}
                              className="text-red-600 hover:text-red-900 transition"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Vue mobile avec vraies stats */}
            <div className="md:hidden">
              {products.map((product) => {
                const stockStatus = getStockStatus(product.stock);
                const realSold = getRealSoldQuantity(product);
                const isRealTime = hasRealStats(product);
                
                return (
                  <div key={product.id} className="border-b border-gray-200 p-4">
                    <div className="flex items-start space-x-3">
                      <img 
                        className="h-16 w-16 rounded-lg object-cover border flex-shrink-0"
                        src={productService.getImageUrl(product.image_url)} 
                        alt={product.name}
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder-image.jpg';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">{product.name}</h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                        
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-xs">
                            <span className="font-semibold text-terracotta">{product.price.toLocaleString()} FCFA</span>
                            <span className="text-gray-600">Stock: {product.stock}</span>
                            {/* ✅ CORRECTION PRINCIPALE - Mobile aussi */}
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-600">Vendus: {realSold}</span>
                              {isRealTime && useRealStats && (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              )}
                            </div>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                            {stockStatus.text}
                          </span>
                        </div>
                        
                        <div className="mt-3 flex justify-end space-x-2">
                          <Link 
                            to={`/edit-product/${product.id}`}
                            className="text-olive hover:text-terracotta transition text-sm font-medium"
                          >
                            Modifier
                          </Link>
                          <button 
                            onClick={() => handleProductDeleted(product.id)}
                            className="text-red-600 hover:text-red-900 transition text-sm font-medium"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MyProductsPage;